<svelte:options
	customElement={{
		tag: 'rf-parallax',
		shadow: 'open',
	}}
/>

<script module>
	export type ReframeParallaxProps = {
		id?: string;
		tag?: string;
		direction?: 'horizontal' | 'vertical';
		'parallax-effect'?: 'scroll' | 'clip' | 'clip-reverse' | 'fade';
		'scroll-element-offset'?: string;
		'parallax-interpolation'?: number;
		'proxy-progress'?: boolean;
		'canvas-fit'?: 'contain' | 'cover';
		'stepped-transition-duration'?: number;
		stepped?: boolean;
		debug?: boolean;
		responsive?: boolean;
		'no-collection'?: boolean;
	};
</script>

<script lang="ts">
	/**
	 * Import
	 */
	import classNames from 'classnames';
	import { onDestroy, onMount } from 'svelte';
	import { get } from 'svelte/store';
	import { useScrollProgressOfElement } from '@src/composables/useScrollProgressOfElement.svelte';
	import { useSlottedElements } from '@src/composables/useSlottedElements';
	import cssVariables from '@src/utils/cssvariables';
	import { parseParameter } from '@src/utils/parseparameter';
	import math from '@src/utils/math';
	import RfParallaxDebug from './rf-parallax-debug.svelte';
	import { SvelteMap } from 'svelte/reactivity';
	import throttled from '@src/utils/throttled';
	import parseBool from '@src/utils/bool';
	import { offset } from '@src/utils/offset';
	import { BREAKPOINTS } from '@src/utils/breakpoints';
	import debounced from '@src/utils/debounced';
	import { v4 as uuidv4 } from 'uuid';
	import { useCollection } from '@src/composables/useCollection.svelte.ts';

	/**
	 * Props
	 */
	let {
		id = '',
		tag = 'section',
		direction = 'vertical',
		'parallax-effect': parallaxEffect = 'scroll',
		'scroll-element-offset': scrollElOffset = '100svh',
		'parallax-interpolation': interpolation = 1,
		'proxy-progress': proxyProgress = false,
		'canvas-fit': canvasFit = 'cover',
		'stepped-transition-duration': steppedTransitionDuration = 500,
		stepped = false,
		debug = false,
		responsive = true,
		'no-collection': noCollection = false,
	}: ReframeParallaxProps = $props();

	/**
	 * Constants
	 */
	const name = 'rf-parallax';
	const componentId = uuidv4();

	/**
	 * Variables
	 */
	let totalHeight = $state(0);
	let totalElements = $state(0);
	let parallaxProxyProgress = $state(0);
	let resizeObserver: ResizeObserver | null = null;
	let parallaxContainer: HTMLElement | null = null;
	let previousStep = -1;
	let rafId: number | null = null;

	const { element, forEachSlottedElement, getSlottedElements } = useSlottedElements();
	const elementsCoordinates = $state(new SvelteMap<number, { center: number; start: number; end: number }>());

	/**
	 * Composables
	 */

	export const collection = parseBool(noCollection)
		? undefined
		: useCollection(id, {
				element,
				moduleName: name,
				componentId,
			});

	/**
	 * Methods
	 */

	/** Handles the parallax effect for "clip" and "fade" modes by applying appropriate CSS classes
	 * to the slotted elements
	 * @param _progress (number): The normalized progress value (0 to 1).
	 * @param numberOfElements (number): The total number of elements in the slotted elements.
	 * @param currentStep (number): The current step of the effect.
	 */

	const setClipOrFadeClasses = (_progress: number, currentStep: number, numberOfElements: number) => {
		if (currentStep !== previousStep) {
			forEachSlottedElement((el, i) => {
				let offsetInByElement = 0;
				let offsetOutByElement = 0;

				const fraction = 1 / numberOfElements;
				const elementProgress = math.clampedMapLinear(
					_progress,
					i / numberOfElements + fraction * offsetInByElement,
					(1 + i) / numberOfElements + fraction * offsetOutByElement,
					0,
					1
				);

				if (el instanceof HTMLElement) {
					el.classList.remove('rf-parallax-active', 'rf-parallax-next', 'rf-parallax-prev');
					if (i === currentStep) {
						el.classList.add('rf-parallax-active');
					} else if (i >= currentStep + 1) {
						el.classList.add('rf-parallax-next');
					} else if (i <= currentStep - 1) {
						el.classList.add('rf-parallax-prev');
					}
					if (elementProgress > 0 || elementProgress < 1) {
						if (!stepped) {
							el.style.setProperty('--rf-parallax-progress', elementProgress.toString());
						}
					}
				}
			});
			if (stepped) {
				previousStep = currentStep;
			}
		}
	};

	/**
	 * Throttled version of setClipOrFadeClasses
	 */
	const throttledSetClipOrFadeClasses = throttled(setClipOrFadeClasses, steppedTransitionDuration);

	/**
	 * Clip or fade progress throttled or not based on stepped
	 */
	const clipOrFadeProgress = (_progress: number) => {
		const numberOfElements = parallaxEffect.includes('clip') && !parseBool(stepped) ? totalElements - 1 : totalElements;
		const currentStep = Math.min(numberOfElements, Math.floor(math.clampedMapLinear(_progress, 0, 1, 0, 0.9999) * numberOfElements));

		if (stepped) {
			updateClipOrFadeProgress(_progress, currentStep, numberOfElements);
		} else {
			setClipOrFadeClasses(_progress, currentStep, numberOfElements);
		}
	};

	/**
	 * Update function for clip or fade progress
	 */
	const updateClipOrFadeProgress = (_progress: number, currentStep: number, numberOfElements: number) => {
		if (rafId) cancelAnimationFrame(rafId);
		rafId = requestAnimationFrame(() => {
			throttledSetClipOrFadeClasses(_progress, currentStep, numberOfElements);
			updateClipOrFadeProgress(_progress, currentStep, numberOfElements);
		});
	};

	/**
	 * Calculates and updates the coordinates of slotted elements for the scroll parallaxEffect
	 *  determining their center, start, and end positions relative to the container.
	 */
	const updateElementsCoordinates = () => {
		const currentElement = get(element);
		const isHorizontal = direction === 'horizontal';
		if (!currentElement) return;
		// Get the total height of the container
		if (isHorizontal && parallaxContainer) {
			totalHeight = parallaxContainer.getBoundingClientRect().width;
		} else {
			totalHeight = currentElement.clientHeight;
		}

		// Iterate over each slotted element and calculate its center, start, and end positions
		forEachSlottedElement(async (el, i) => {
			if (el instanceof HTMLElement) {
				let elementSize = 0;
				let elementCenter = 0;
				let parentOffset = isHorizontal ? window.innerWidth : window.innerHeight;
				const offsetTopParent = offset(currentElement).offsetTop;
				const offsetTopChild = offset(el).offsetTop;
				const trueOffsetTop = offsetTopChild - offsetTopParent;

				if (isHorizontal) {
					elementSize = el.clientWidth / totalHeight;
					elementCenter = el.offsetLeft / totalHeight + elementSize / 2;
				} else {
					elementSize = el.clientHeight / totalHeight;
					elementCenter = trueOffsetTop / totalHeight + elementSize / 2;
				}

				const parsedOffset = parseParameter(scrollElOffset);
				// Calculate the offset of the element based on the unit and direction
				if (isHorizontal) {
					if (parsedOffset.unit === 'vw') {
						parentOffset = (window.innerWidth * parsedOffset.number) / 100;
					} else if (parsedOffset.unit === '%') {
						parentOffset = (el.clientWidth * parsedOffset.number) / 100;
					} else if (parsedOffset.unit === 'px') {
						parentOffset = parsedOffset.number;
					}
				} else {
					if (parsedOffset.unit === 'vh') {
						parentOffset = (window.innerHeight * parsedOffset.number) / 100;
					} else if (parsedOffset.unit === '%') {
						parentOffset = (el.clientHeight * parsedOffset.number) / 100;
					} else if (parsedOffset.unit === 'px') {
						parentOffset = parsedOffset.number;
					}
				}

				const elementOffset = parentOffset / totalHeight / 2;
				const elementStart = elementCenter - elementOffset;
				const elementEnd = elementCenter + elementOffset;

				// Update the coordinates map
				// Add the calculated coordinates to the coordinates map
				elementsCoordinates.set(i, { center: elementCenter, start: elementStart, end: elementEnd });
			}
		});
	};

	/**
	 * Updates the parallax effect for the "scroll" mode
	 * by applying a calculated progress value to each slotted elements.
	 */
	const scrollProgress = (_progress: number) => {
		updateElementsCoordinates();

		forEachSlottedElement(async (el, i) => {
			if (el instanceof HTMLElement) {
				const { start, end } = elementsCoordinates.get(i) || { start: 0, end: 0 };
				const elementProgress = math.clampedMapLinear(_progress, start, end, 1, -1);
				if (elementProgress > 0 || elementProgress < 1) {
					el.style.setProperty('--rf-parallax-progress', elementProgress.toString());
				}
			}
		});
	};

	/**
	 * Exports the onProgress method for external use.
	 * @param _progress - The progress value to apply to the parallax effect from 0 to 1.
	 */
	export const onProgress = (_progress: number = 0) => {
		const currentContainer = get(element);
		if (proxyProgress && currentContainer) {
			parallaxProxyProgress = math.decimals(_progress, 4) ?? 0;
		}
		if (parallaxEffect.includes('clip') || parallaxEffect.includes('fade')) {
			clipOrFadeProgress(_progress);
		} else if (parallaxEffect === 'scroll') {
			scrollProgress(_progress);
		}
	};

	/**
	 * Returns the offsetEnd, offsetStart and triggerPosition params for the scroll progress
	 * based on the parallax effect and the stepped prop
	 * @returns { offsetEnd: number, offsetStart: number, triggerPosition: string }
	 */
	const getScrollProgressParam = (): { offsetEnd: number; offsetStart: number; triggerPosition: string } => {
		let offsetEnd = 0;
		let offsetStart = 0;
		let triggerPosition = '50vh';
		if (parallaxEffect === 'fade' || parallaxEffect.includes('clip')) {
			offsetEnd = -1;
		}
		if (parallaxEffect === 'scroll' && direction === 'horizontal') {
			triggerPosition = '0vh';
			offsetEnd = -1;
		}
		if (parallaxEffect !== 'scroll') {
			triggerPosition = '0vh';
		}
		if (parallaxEffect === 'fade' && !parseBool(stepped)) {
			offsetStart = -1;
		}

		return { offsetEnd, offsetStart, triggerPosition };
	};

	/**
	 * Composables
	 */
	if (!proxyProgress) {
		useScrollProgressOfElement({
			debug,
			el: element,
			interpolation,
			onScroll: onProgress,
			offsetStart: getScrollProgressParam().offsetStart,
			offsetEnd: getScrollProgressParam().offsetEnd,
			triggerPosition: getScrollProgressParam().triggerPosition,
		});
	}
	/**
	 * Effects
	 */
	$effect(() => {
		if (parallaxEffect.includes('clip') || parallaxEffect.includes('fade')) {
			forEachSlottedElement((el) => {
				if (el.nodeName === 'RF-CANVAS' && el instanceof HTMLElement) {
					el.setAttribute('fit', canvasFit ?? 'cover');
					el.setAttribute('interactive', 'false');
				}
			});
		}
	});

	const clipOrFadeResize = () => {
		const currentContainer = get(element);
		if (!currentContainer) return;
		if (currentContainer.clientWidth < BREAKPOINTS.xs && responsive) {
			forEachSlottedElement((el) => {
				if (el.nodeName === 'RF-CANVAS' && el instanceof HTMLElement) {
					el.setAttribute('fit', 'contain');
				}
			});
		}
	};

	const clipOrFadeResizeDebounced = debounced(clipOrFadeResize, 100);

	/**
	 * Lifecycle
	 */
	onMount(() => {
		const currentContainer = get(element);
		if (!currentContainer) return;
		totalElements = getSlottedElements().length;
		forEachSlottedElement((el: Element, i) => {
			if (el instanceof HTMLElement) {
				if (i === 0) {
					el.classList.add('rf-parallax-active');
				}
				el.style.setProperty('--rf-parallax-index', i.toString());
			}
		});

		// If the direction is horizontal and the parallaxEffect is "scroll", observe the container for resize changes
		if (direction === 'horizontal' && parallaxEffect === 'scroll' && parallaxContainer) {
			resizeObserver = new ResizeObserver(updateElementsCoordinates);
			resizeObserver.observe(parallaxContainer);
		} else if ((parallaxEffect.includes('clip') || parallaxEffect.includes('fade')) && responsive) {
			resizeObserver = new ResizeObserver(clipOrFadeResizeDebounced);
			resizeObserver.observe(currentContainer);
		}
	});

	onDestroy(() => {
		if (resizeObserver) resizeObserver?.disconnect();
	});
</script>

<slot name="header"></slot>
<svelte:element
	this={tag}
	bind:this={$element}
	class={classNames('parallax', direction, parallaxEffect, { stepped: parseBool(stepped), responsive: parseBool(responsive) })}
	style={cssVariables({
		'--total-elements': totalElements.toString(),
		'--total-height': totalHeight.toString(),
		'--stepped-transition-duration': steppedTransitionDuration.toString() + 'ms',
		...(proxyProgress ? { '--rf-scroll-progress': parallaxProxyProgress } : {}),
	})}
	part="parallax"
>
	{#if parallaxEffect === 'scroll' && direction === 'vertical' && debug}
		<RfParallaxDebug coordinates={elementsCoordinates} />
	{/if}
	<div class="parallax__wrapper">
		<div class="parallax__container" bind:this={parallaxContainer}>
			<slot></slot>
		</div>
	</div>
</svelte:element>
<slot name="footer"></slot>

<style lang="scss" global>
	@use 'assets/scss/core/rf-breakpoints' as breakpoint;

	@mixin parallax-slotted {
		::slotted(*.rf-parallax-next),
		::slotted(*.rf-parallax-prev),
		::slotted(*.rf-parallax-active) {
			@content;
		}
	}
	.parallax {
		height: 100%;
		--rf-parallax-gap: var(--rf-row-gap, 20px);
		width: 100%;
		margin: 0 auto;

		&.vertical.scroll {
			max-width: var(--rf-container-max-width, 100%);
			position: relative;
			display: flex;
			flex-direction: column;
			gap: var(--rf-parallax-gap);
			::slotted(*) {
				display: block;
				transform: translateY(calc(var(--rf-parallax-progress, 0) * var(--rf-parallax-element-translate-distance, 25vh)));
				width: calc(100% * var(--rf-parallax-columns, 6) / var(--rf-columns) - var(--rf-parallax-gap));
			}
			::slotted(*:nth-child(even)) {
				margin-left: auto;
			}
			&.responsive {
				@container rf-parallax (width < #{breakpoint.$xs}) {
					--rf-parallax-columns: 11;
					--rf-parallax-element-translate-distance: 10vw;
				}
			}
		}
		&.horizontal.scroll {
			height: calc(var(--total-height) * 1px);
			.parallax__wrapper {
				top: 0;
				position: sticky;
				overflow: hidden;
				height: 100svh;
			}
			.parallax__container {
				height: 100%;
				display: flex;
				gap: var(--rf-parallax-gap);
				width: fit-content;
				position: relative;
				transform: translateX(calc(var(--rf-scroll-progress, 0) * (-100% + 100vw)));
				::slotted(*) {
					max-height: 100%;
					display: block;
					flex-shrink: 0;
					flex-grow: 0;
					align-self: var(--rf-parallax-element-self-align, center);
					transform: translateX(calc(var(--rf-parallax-progress, 0) * var(--rf-parallax-element-translate-distance, 25vw)));
					width: calc(100vw * var(--rf-parallax-columns, 6) / var(--rf-columns) - var(--rf-parallax-gap));
				}
			}
			&.responsive {
				::slotted(*) {
					@container rf-parallax (width < #{breakpoint.$xs}) {
						--rf-parallax-columns: 11;
					}
				}
			}
		}
		&.fade,
		&.clip,
		&.clip-reverse {
			.parallax__wrapper {
				display: flex;
				height: var(--rf-parallax-wrapper-height, calc(var(--total-elements) * 100svh));
			}
			.parallax__container {
				top: 0;
				position: sticky;
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				height: var(--rf-parallax-container-height, 100svh);
				width: 100%;
			}

			::slotted(*) {
				--rf-canvas-height: 100%;
				display: block;
				position: absolute;
				top: 0;
				left: 0;
				max-height: 100svh;
				height: 100%;
				width: 100%;
				object-fit: cover;
			}
			::slotted(*:not(.rf-parallax-active)) {
				pointer-events: none;
			}
		}
		&.fade {
			&:not(.stepped) {
				::slotted(*) {
					z-index: var(--rf-parallax-index);
					opacity: calc(var(--rf-parallax-progress));
				}
			}
			&.stepped {
				::slotted(*) {
					transition: opacity 0.5s ease-in-out;
					opacity: 0;
				}
				::slotted(*.rf-parallax-active) {
					opacity: 1;
				}
			}
		}
		&.clip-reverse,
		&.clip {
			@include parallax-slotted {
				z-index: calc(var(--total-elements) - var(--rf-parallax-index) + 1);
			}
			::slotted(*:not(.rf-parallax-active):not(.rf-parallax-next):not(.rf-parallax-prev)) {
				opacity: 0;
			}
			&.stepped {
				::slotted(*) {
					will-change: clip-path;
					transition: clip-path var(--stepped-transition-duration) ease-in-out;
				}
				::slotted(*.rf-parallax-active) {
					clip-path: inset(0 0 0 0);
				}
			}
		}
		&.vertical.clip-reverse {
			&:not(.stepped) {
				@include parallax-slotted {
					clip-path: inset(calc(var(--rf-parallax-progress) * 100%) 0 0 0);
				}
			}
			&.stepped {
				::slotted(*.rf-parallax-prev) {
					clip-path: inset(100% 0 0 0);
				}
			}
		}
		&.vertical.clip {
			&:not(.stepped) {
				@include parallax-slotted {
					clip-path: inset(0 0 calc(var(--rf-parallax-progress) * 100%) 0);
				}
			}
			&.stepped {
				::slotted(*.rf-parallax-prev) {
					clip-path: inset(0 0 100% 0);
				}
				::slotted(*.rf-parallax-next) {
					clip-path: inset(100% 0 0 0);
				}
			}
		}
		&.horizontal.clip {
			&:not(.stepped) {
				@include parallax-slotted {
					clip-path: inset(0 calc(var(--rf-parallax-progress) * 100%) 0 0);
				}
			}
			&.stepped {
				::slotted(*.rf-parallax-prev) {
					clip-path: inset(0 100% 0 0);
				}
			}
		}
		&.horizontal.clip-reverse {
			&:not(.stepped) {
				@include parallax-slotted {
					clip-path: inset(0 0 0 calc(var(--rf-parallax-progress) * 100%));
				}
			}
			&.stepped {
				::slotted(*.rf-parallax-prev) {
					clip-path: inset(0 0 0 100%);
				}
			}
		}
	}
</style>
